// /src/contexts/ProfileContext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { supabase } from "../supabaseClient";
import { useSupabaseAuth } from "./SupabaseAuthContext";
import { prepareSessionForProfile } from "../utils/prepareSessionForProfile";

export const ProfileContext = createContext();

export function ProfileProvider({ children }) {
  const { user, loading: authLoading } = useSupabaseAuth();
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(
    () => localStorage.getItem("profileId") || ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [wordlists, setWordlists] = useState([]);
  const [wordlistsLoading, setWordlistsLoading] = useState(false);

  // Fetch all profiles linked to the current user
  const fetchProfiles = useCallback(async () => {
    if (!user) {
      setProfiles([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { data, error: fetchError } = await supabase
        .from("user_profiles")
        .select(
          `
          profile_id,
          profiles (profile_id, created_at, name, is_male, avatar_url, intro_done)
        `
        )
        .eq("user_id", user.id);

      if (fetchError) {
        console.error("[Error] Failed to fetch profiles:", fetchError);
        setError(fetchError.message);
        setProfiles([]);
        setLoading(false);
        return;
      }

      // data is an array of user_profiles; each row includes a "profiles" object
      const mapped = data.map((row) => row.profiles);
      setProfiles(mapped);
    } catch (err) {
      console.error("[Error] Unexpected error in fetchProfiles:", err);
      setError(err.message || "An unexpected error occurred");
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch all available wordlists
  const fetchWordlists = useCallback(async () => {
    setWordlistsLoading(true);

    try {
      const { data, error: fetchError } = await supabase
        .from("wordlists")
        .select("*")
        .order("name");

      if (fetchError) {
        console.error("[Error] Failed to fetch wordlists:", fetchError);
        setWordlists([]);
      } else {
        setWordlists(data || []);
      }
    } catch (err) {
      console.error("[Error] Unexpected error in fetchWordlists:", err);
      setWordlists([]);
    } finally {
      setWordlistsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    fetchProfiles();
    fetchWordlists();
  }, [fetchProfiles, fetchWordlists, authLoading]);

  // Create a new profile & link it to current user
  const createProfile = async ({ name, gender, wordlistId }) => {
    if (!user || !wordlistId) {
      console.error("[Error] Missing user or wordlistId for profile creation");
      return null;
    }

    setError("");
    setLoading(true);

    try {
      // Get the current session to include the auth token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error("[Error] Error getting session:", sessionError);
        setLoading(false);
        setError(sessionError?.message || "Failed to get session");
        return null;
      }

      if (!session?.access_token) {
        setLoading(false);
        setError("No authentication token available");
        return null;
      }

      // Call the Edge Function for steps 1-3 (create profile, link to user, link wordlist)
      const { data, error: edgeFunctionError } =
        await supabase.functions.invoke("create_profile", {
          body: { name, gender, wordlistId },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

      if (edgeFunctionError) {
        console.error("[Error] Edge Function error:", edgeFunctionError);
        setLoading(false);
        setError(edgeFunctionError?.message || "Failed to create profile");
        return null;
      }

      if (!data?.success || !data?.profile) {
        console.error("[Error] Edge Function did not return a profile");
        setLoading(false);
        setError("Edge Function did not return a profile");
        return null;
      }

      const newProfile = data.profile;

      // 4. Prepare session for profile
      try {
        await prepareSessionForProfile(newProfile.profile_id);
      } catch (sessionError) {
        console.error(
          "[Error] Failed to prepare session for profile:",
          sessionError
        );
        setLoading(false);
        setError(
          sessionError?.message || "Failed to prepare session for profile"
        );
        return null;
      }

      // 5. Refresh the profile list
      await fetchProfiles();
      setLoading(false);
      return newProfile;
    } catch (err) {
      console.error("[Error] Unexpected error in profile creation:", err);
      setLoading(false);
      setError(err.message || "An unexpected error occurred");
      return null;
    }
  };

  const selectedProfile =
    profiles.find((p) => p.profile_id === selectedProfileId) || null;

  const setSelectedProfile = (profileId) => {
    setSelectedProfileId(profileId);
    localStorage.setItem("profileId", profileId);
  };

  const value = {
    profiles,
    loading,
    error,
    selectedProfileId,
    selectedProfile,
    setSelectedProfile,
    createProfile,
    fetchProfiles,
    wordlists,
    wordlistsLoading,
  };

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}

export function useProfile() {
  return useContext(ProfileContext);
}
